import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { CircleX, MailQuestion } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, OnboardingProgressStep, useProgress } from '@/context/progress';
import BaseLayout from '@/layouts/base';

const DEFAULT_ERROR_KEY = 'default';

const ERROR_KEY_PROGRESS = {
  auth0_email_not_confirmed: 0.5,
  [DEFAULT_ERROR_KEY]: 0.25,
};

const ERROR_KEY_ICONS = {
  auth0_email_not_confirmed: MailQuestion,
  [DEFAULT_ERROR_KEY]: CircleX,
};

/**
 * Because a user isn't "technically" logged in if they haven't verified their email (as we redirect to this page
 * during the login process (see actions in Auth0)), we can't use the standard OIDC signout methods to log the user out
 * as "technically" their session doesn't exist (or exist outside of Auth0 anyway).
 *
 * Instead, we have to trigger a logout via the Auth0 URL instead.
 * See https://auth0.com/docs/authenticate/login/logout/log-users-out-of-auth0
 *
 * Note that if we didn't show a Logout button here the user is technically stuck as they're always redirected back
 * to this page (the only exception would be if they've manually cleared their browser data).
 */
const StatusPageLogoutButton = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const errorKeyParam = searchParams.get('error_key') ?? DEFAULT_ERROR_KEY;

  if (errorKeyParam !== 'auth0_email_not_confirmed') return null;

  return (
    <Button asChild>
      <a href={`${import.meta.env.VITE_APP_AUTH_URL}/oidc/logout`}>{t('logout')}</a>
    </Button>
  );
};
StatusPageLogoutButton.displayName = 'StatusPageLogoutButton';

const StatusPage = () => {
  const [searchParams] = useSearchParams();
  const { setCurrentStep } = useProgress();
  const { i18n, t } = useTranslation();
  const errorKeyParam = searchParams.get('error_key') ?? DEFAULT_ERROR_KEY;
  const errorKey = i18n.exists(`errorPage.${errorKeyParam}`) ? errorKeyParam : DEFAULT_ERROR_KEY;
  const currentStep =
    errorKey !== DEFAULT_ERROR_KEY
      ? searchParams.get('institution') === 'true'
        ? InstitutionOnboardingProgressStep.INSTITUTION_AUTHENTICATION
        : OnboardingProgressStep.AUTHENTICATION
      : null;
  const progress = errorKey in ERROR_KEY_PROGRESS ? ERROR_KEY_PROGRESS[errorKey as keyof typeof ERROR_KEY_PROGRESS] : ERROR_KEY_PROGRESS[DEFAULT_ERROR_KEY];
  const Icon = errorKey in ERROR_KEY_ICONS ? ERROR_KEY_ICONS[errorKey as keyof typeof ERROR_KEY_ICONS] : ERROR_KEY_ICONS[DEFAULT_ERROR_KEY];

  useEffect(() => {
    if (!currentStep) return;

    return setCurrentStep(currentStep, progress);
  }, [currentStep, progress, setCurrentStep]);

  return (
    <BaseLayout className="-md:justify-center space-y-8" logoutButton={<StatusPageLogoutButton />} size="sm">
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <Icon />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t(`errorPage.${errorKey}.title`)}</PageHeadingTitle>
        <PageHeadingDescription>{t(`errorPage.${errorKey}.description`)}</PageHeadingDescription>
      </PageHeading>
    </BaseLayout>
  );
};
StatusPage.displayName = 'StatusPage';

export default StatusPage;
